/* ============================================================
   AXION // SPECIFICATION — Layer 4: LOAD-BEARING
   The page's structural chrome is drawn like an engineering
   plan: members carry load and are dimensioned + annotated,
   assembled with a mechanical (stepped, plotter-like) reveal.
     1 dimensioned section dividers (beams, off the right rail)
     2 load-bearing CTAs (corner tick + annotation + hard press)
     3 footer base-plate (foundation dimension line)
     4 header top-chord (dimensioned header height, fixed)
     5 margin dimension rail (right gutter, full height)
   Self-contained (injects own CSS). Non-invasive: a pointer-
   events:none overlay in the gutters — never alters flow or
   covers content. Desktop-only, reduced-motion safe. Coherent
   with L1 (instrument frame) + L3 (PARSE). Loaded last.
   ============================================================ */
(function () {
  'use strict';
  var mq = window.matchMedia;
  var REDUCE = mq && mq('(prefers-reduced-motion: reduce)').matches;
  if (mq && mq('(max-width: 999px)').matches) return;   // tablet/mobile stay clean

  var css = [
    ':root{--lb-line:var(--instr-line,rgba(23,20,15,.14));--lb-read:var(--instr-read,rgba(23,20,15,.40));',
      '--lb-accent:var(--color-accent,#A67C4A);--lb-mono:var(--font-instr,ui-monospace,Menlo,Consolas,monospace);--lb-rail:20px;}',
    '.ax-lb{position:absolute;top:0;left:0;width:100%;z-index:4;pointer-events:none;font-family:var(--lb-mono);}',
    '.ax-lb *{pointer-events:none;}',
    /* 5 — margin dimension rail (right gutter, full height) */
    '.ax-lb__rail{position:absolute;top:0;bottom:0;right:var(--lb-rail);width:1px;background:var(--lb-line);}',
    '.ax-lb__rail::before,.ax-lb__rail::after{content:"";position:absolute;left:-3px;width:7px;height:1px;background:var(--lb-read);}',
    '.ax-lb__rail::before{top:0;}.ax-lb__rail::after{bottom:0;}',
    '.ax-lb__railcap{position:absolute;right:calc(var(--lb-rail) - 4px);width:9px;height:1px;background:var(--lb-read);}',
    '.ax-lb__raillbl{position:absolute;right:calc(var(--lb-rail) + 9px);white-space:nowrap;transform-origin:right top;',
      'transform:rotate(-90deg) translateY(-3px);font-size:8.5px;letter-spacing:.22em;text-transform:uppercase;color:var(--lb-read);}',
    /* 1 — section stations (beams off the rail) */
    '.ax-lb__station{position:absolute;right:var(--lb-rail);height:1px;}',
    '.ax-lb__station i{position:absolute;right:0;top:0;height:1px;width:0;background:var(--lb-accent);',
      'transition:width .5s steps(7);}',
    '.ax-lb__station.is-in i{width:34px;}',
    '.ax-lb__station b{position:absolute;right:42px;top:-5px;white-space:nowrap;font-weight:400;',
      'font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:var(--lb-read);opacity:0;transition:opacity .01s steps(1) .25s;}',
    '.ax-lb__station.is-in b{opacity:1;}',
    '.ax-lb__station em{position:absolute;right:42px;top:7px;white-space:nowrap;font-style:normal;',
      'font-size:8px;letter-spacing:.14em;color:var(--lb-accent);opacity:0;transition:opacity .01s steps(1) .34s;}',
    '.ax-lb__station.is-in em{opacity:.85;}',
    /* 4 — header top-chord (fixed, dimensions the header) */
    '.ax-lb__chord{position:fixed;left:34px;right:var(--lb-rail);height:1px;z-index:4;pointer-events:none;}',
    '.ax-lb__chord::before{content:"";position:absolute;left:0;right:0;top:0;height:1px;background:var(--lb-line);',
      'transform:scaleX(0);transform-origin:left;transition:transform .6s steps(9);}',
    '.ax-lb__chord.is-in::before{transform:scaleX(1);}',
    '.ax-lb__chord i{position:absolute;top:-4px;width:1px;height:9px;background:var(--lb-read);}',
    '.ax-lb__chord i.l{left:0;}.ax-lb__chord i.r{right:0;}',
    '.ax-lb__chord b{position:absolute;left:50%;top:-15px;transform:translateX(-50%);background:var(--color-background,#FAFAF8);',
      'padding:0 8px;font-size:8.5px;letter-spacing:.24em;text-transform:uppercase;color:var(--lb-read);opacity:0;transition:opacity .01s steps(1) .4s;}',
    '.ax-lb__chord.is-in b{opacity:1;}',
    '.ax-lb__chord b s{color:var(--lb-accent);text-decoration:none;}',
    /* 3 — footer base-plate (foundation) */
    '.ax-lb__plate{position:absolute;left:34px;right:var(--lb-rail);height:14px;}',
    '.ax-lb__plate::before{content:"";position:absolute;left:0;right:0;top:0;height:1px;background:var(--lb-read);',
      'transform:scaleX(0);transform-origin:center;transition:transform .7s steps(11);}',
    '.ax-lb__plate.is-in::before{transform:scaleX(1);}',
    '.ax-lb__plate u{position:absolute;top:0;width:1px;height:7px;background:var(--lb-read);text-decoration:none;}',
    '.ax-lb__plate b{position:absolute;left:50%;top:5px;transform:translateX(-50%);white-space:nowrap;background:var(--color-background,#FAFAF8);',
      'padding:0 10px;font-size:9px;letter-spacing:.26em;text-transform:uppercase;color:var(--lb-read);opacity:0;transition:opacity .01s steps(1) .45s;}',
    '.ax-lb__plate.is-in b{opacity:1;}',
    '.ax-lb__plate b s{color:var(--lb-accent);text-decoration:none;}',
    /* 2 — load-bearing CTAs */
    '.ax-lb-cta{position:relative;}',
    '.ax-lb-cta::before{content:"";position:absolute;left:-5px;top:-5px;width:7px;height:7px;',
      'border-left:1px solid var(--lb-accent);border-top:1px solid var(--lb-accent);opacity:.0;transition:opacity .25s steps(2);}',
    '.ax-lb-cta::after{content:"";position:absolute;right:-5px;bottom:-5px;width:7px;height:7px;',
      'border-right:1px solid var(--lb-accent);border-bottom:1px solid var(--lb-accent);opacity:0;transition:opacity .25s steps(2);}',
    '.ax-lb-cta.is-in::before,.ax-lb-cta.is-in::after{opacity:.7;}',
    '.ax-lb-cta__n{position:absolute;left:0;top:calc(100% + 7px);font-family:var(--lb-mono);font-size:8.5px;',
      'letter-spacing:.2em;text-transform:uppercase;color:var(--lb-read);white-space:nowrap;opacity:0;',
      'transition:opacity .01s steps(1) .2s;pointer-events:none;}',
    '.ax-lb-cta__n s{color:var(--lb-accent);text-decoration:none;}',
    '.ax-lb-cta.is-in .ax-lb-cta__n{opacity:1;}',
    '.ax-lb-cta:active{transform:translateY(2px);transition:transform 0s;}',   /* mechanical, no bounce */
    REDUCE ? '.ax-lb *{transition:none!important;}' : ''
  ].join('');
  var st = document.createElement('style'); st.textContent = css; document.head.appendChild(st);

  function pad2(n){ return (n < 10 ? '0' : '') + n; }

  var root = document.createElement('div'); root.className = 'ax-lb'; root.setAttribute('aria-hidden', 'true');
  document.body.appendChild(root);

  var header = document.querySelector('header.ax-header');
  var foot = document.querySelector('footer.foot');
  var main = document.querySelector('#main') || document.body;

  // ── 5 · margin rail ──────────────────────────────────────
  var rail = document.createElement('span'); rail.className = 'ax-lb__rail'; root.appendChild(rail);
  var rlbl = document.createElement('span'); rlbl.className = 'ax-lb__raillbl';
  rlbl.textContent = 'AXION // LOAD PLAN · ' + window.innerWidth + 'PX';
  rlbl.style.top = '120px'; root.appendChild(rlbl);

  // ── 1 · section stations ─────────────────────────────────
  var blocks = [].slice.call(main.children).filter(function (el) {
    return /^(SECTION|DIV)$/.test(el.tagName) && el.offsetHeight > 260 && !el.classList.contains('ax-lb');
  });
  var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (es) {
    es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
  }, { threshold: 0, rootMargin: '0px 0px -22% 0px' }) : null;

  blocks.forEach(function (el, i) {
    var top = el.getBoundingClientRect().top + window.scrollY;
    var h = Math.round(el.offsetHeight);
    var s = document.createElement('span'); s.className = 'ax-lb__station'; s.style.top = top + 'px';
    s.innerHTML = '<i></i><b>§ ' + pad2(i + 1) + ' / ' + pad2(blocks.length) + '</b><em>BEAM · H' + h + '</em>';
    root.appendChild(s);
    if (io) io.observe(s); else s.classList.add('is-in');
  });

  // ── 4 · header top-chord (fixed) ─────────────────────────
  if (header) {
    var hH = Math.round(header.getBoundingClientRect().height);
    var chord = document.createElement('span'); chord.className = 'ax-lb__chord'; chord.style.top = hH + 'px';
    chord.innerHTML = '<i class="l"></i><i class="r"></i><b>HEADER <s>▭ ' + hH + 'PX</s></b>';
    document.body.appendChild(chord);
    requestAnimationFrame(function () { setTimeout(function () { chord.classList.add('is-in'); }, 350); });
  }

  // ── 3 · footer base-plate ────────────────────────────────
  if (foot) {
    var ftop = foot.getBoundingClientRect().top + window.scrollY;
    var plate = document.createElement('span'); plate.className = 'ax-lb__plate'; plate.style.top = ftop + 'px';
    plate.innerHTML = '<u style="left:0"></u><u style="left:33%"></u><u style="left:66%"></u><u style="right:0"></u>' +
      '<b>⊥ FOUNDATION · BASE PLATE <s>VOL II</s></b>';
    root.appendChild(plate);
    if (io) io.observe(plate); else plate.classList.add('is-in');
  }

  // ── 2 · load-bearing CTAs ────────────────────────────────
  var notes = { 'btn-primary': '↧ LOAD-BEARING · PRIMARY', 'ax-news__btn': '↧ SUBSCRIBE' };
  ['.btn-primary', '.ax-news__btn'].forEach(function (sel) {
    document.querySelectorAll(sel).forEach(function (b) {
      if (b.__lb) return; b.__lb = 1; b.classList.add('ax-lb-cta');
      var key = sel.replace(/^\./, '');
      var n = document.createElement('span'); n.className = 'ax-lb-cta__n';
      n.innerHTML = '<s>' + notes[key].charAt(0) + '</s>' + notes[key].slice(1);
      b.appendChild(n);
      if (io) io.observe(b); else b.classList.add('is-in');
    });
  });

  // keep document-anchored annotations aligned if layout shifts (fonts/images)
  function relayout() {
    root.style.height = document.documentElement.scrollHeight + 'px';
    var st2 = root.querySelectorAll('.ax-lb__station');
    blocks.forEach(function (el, i) { if (st2[i]) st2[i].style.top = (el.getBoundingClientRect().top + window.scrollY) + 'px'; });
    var pl = root.querySelector('.ax-lb__plate'); if (pl && foot) pl.style.top = (foot.getBoundingClientRect().top + window.scrollY) + 'px';
    rail.style.bottom = '0';
  }
  relayout();
  window.addEventListener('load', relayout);
  window.addEventListener('resize', relayout, { passive: true });
  setTimeout(relayout, 1200);
})();
